import React, { useState } from 'react'

import {
  Column,
  CellRenderers,
  SimpleTable,
  SentimentDelta,
  TableHead,
  TableCell,
  makeAppStyles,
  vodafonePalette,
  DefaultCellRenderer,
  Box,
  ButtonPopover,
  CheckboxGroup,
  RoundedPlainTextButton,
  Tooltip,
  SecondaryIconTooltip,
} from '@percept/mui'

import { InfoOutlined, ViewColumnSharp } from '@percept/mui/icons'

import { intersection } from 'lodash-es'

import { getSegmentLabel, vodafoneMarketsById } from './lib'

import { MarketDisplayLabel } from 'components/MarketDisplay'

import { rateDeltaFormatter, rateFormatter } from '@percept/mui/charts'

import { BenchmarkingTableRow } from './typings'


const useStyles = makeAppStyles( theme => ({
  headerCell: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.text.hint}`,
  },
  effectivenessCell: {
    position: 'relative',
    backgroundColor: vodafonePalette.aubergine,
    color: '#FFF',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 14,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: -5,
      height: '100%',
      width: 5,
      backgroundColor: vodafonePalette.aubergine,
    },
  },
  outcomesCell: {
    backgroundColor: vodafonePalette.turquoise,
    color: '#FFF',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 14,
  },
  columGroupCellContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tooltipIcon: {
    marginLeft: theme.spacing(1.5),
    opacity: 0.85,
    fontSize: 18,
  },
  tooltip: {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.getContrastText(theme.palette.action.disabledBackground),
    fontSize: 14,
    padding: theme.spacing(1),
    boxShadow: theme.shadows[3],
  },
}))


const displayNA = <Box color='text.disabled'>N / A</Box>

const renderers: CellRenderers<BenchmarkingTableRow> = {
  /* eslint-disable react/display-name, react/prop-types */
  org_unit_id: ({ org_unit_id }) => {
    const market = vodafoneMarketsById[org_unit_id]
    if( !market ) return null
    return <MarketDisplayLabel {...market} />
  },
  segment_label: (props) => <>{getSegmentLabel(props.segment_label)}</>,
  label: (props) => <>{props.label}</>,
  cvr_change: ({ cvr_change = null}) => {
    if( cvr_change === null ) return displayNA
    return <SentimentDelta value={cvr_change * 100} />
  },
  effectiveness_change: ({ effectiveness_change = null }) => {
    if( effectiveness_change === null ) return displayNA
    return <SentimentDelta value={effectiveness_change * 100} />
  },
  effectiveness_competitor: ({ effectiveness_competitor = null }) => {
    if( effectiveness_competitor === null ){
      return displayNA
    }
    return <>
      {rateDeltaFormatter(effectiveness_competitor)}
    </>
  },
  effectiveness_metric: ({ effectiveness_metric }) => (
    <>{effectiveness_metric}</>
  ),
  effectiveness_market: ({ effectiveness_market = null }) => {
    if( effectiveness_market === null ){
      return displayNA
    }
    return <>
      {rateDeltaFormatter(effectiveness_market)}
    </>
  },
  /* eslint-enable react/display-name, react/prop-types */
}

const defaultRenderer: DefaultCellRenderer<BenchmarkingTableRow> = (row, key) => {
  if( row[key] === null || row[key] === undefined ){
    return displayNA
  }
  return rateFormatter(row[key] as number | null)
}


const costEffectivenessCellStyle = {
  backgroundColor: 'rgba(94,39,80,0.05)',
}

const outcomesCellStyle = {
  backgroundColor: 'rgba(0,124,146,0.1)',
}

type FilterableColumn<Row extends object> = (
  Column<Row> & (
    {
      filterable?: never
      hideInitially?: never
    } | {
      filterable: boolean
      hideInitially?: boolean
    }
  )
)

const columns: FilterableColumn<BenchmarkingTableRow>[] = [
  {
    key: 'label',
    align: 'left',
    label: null,
    // label: 'Market',
  },
  // {
  //   key: 'segment_label',
  //   align: 'left',
  //   label: 'Segment',
  // },
  // {
  //   key: 'effectiveness_metric',
  //   align: 'center',
  //   label: 'Metric',
  //   style: costEffectivenessCellStyle,
  // },
  {
    key: 'effectiveness_competitor',
    align: 'center',
    label: 'Competitor Change',
    filterable: true,
    hideInitially: true,
    style: costEffectivenessCellStyle,
  },
  {
    key: 'effectiveness_market',
    align: 'center',
    label: 'Market Change',
    filterable: true,
    hideInitially: true,
    style: costEffectivenessCellStyle,
  },
  {
    key: 'effectiveness_change',
    align: 'center',
    label: 'Difference',
    filterable: true,
    style: costEffectivenessCellStyle,
  },
  {
    key: 'cvr_previous',
    align: 'center',
    label: 'CVR Start',
    filterable: true,
    hideInitially: true,
    style: outcomesCellStyle,
  },
  {
    key: 'cvr_current',
    align: 'center',
    label: 'CVR End',
    filterable: true,
    hideInitially: true,
    style: outcomesCellStyle,
  },
  {
    key: 'cvr_change',
    align: 'center',
    label: 'Change',
    filterable: true,
    style: outcomesCellStyle,
  },
]

type ColumnGrouping = 'Business Outcomes' | 'Cost Effectiveness'

const columnGroupingMapping: Record<ColumnGrouping, (keyof BenchmarkingTableRow)[]> = {
  'Cost Effectiveness': [
    // 'effectiveness_metric',
    'effectiveness_competitor',
    'effectiveness_market',
    'effectiveness_change',
  ],
  'Business Outcomes': [
    'cvr_previous',
    'cvr_current',
    'cvr_change',
  ],
}


const getTooltipContentMapping = (
  effectivenessMetric: string | null | undefined
): Partial<Record<(keyof BenchmarkingTableRow) | ColumnGrouping, React.ReactNode>> => {
  const metricLabel = effectivenessMetric || 'relevant performance metric'
  return {
    effectiveness_competitor: <>
      Change in competitor {metricLabel} between the selected baseline and comparison periods.
      Negative values show that the {metricLabel} has improved.
    </>,
    effectiveness_market: <>
      Change in the markets CPM between the selected baseline and comparison periods.
      Negative values show that the {metricLabel} has improved.
    </>,
    effectiveness_change: <>
      Difference in the {metricLabel} change between market and competitors.
      Positive values show that the market's {metricLabel} has has a better change over the period than the competitors.
    </>,
    cvr_previous: <>
      Market's conversion rate in the selected baseline time period
    </>,
    cvr_current: <>
      Market's conversion rate in the selected comparison time period
    </>,
    cvr_change: <>
      Percentage change in the market's CVR between the baseline and comparison time periods. 
      Positive values show that the conversion rate has improved
    </>,
    // column grouping tooltips
    'Cost Effectiveness': <>
      This metric evaluates cost effectiveness by comparing internal market cost changes against
      competitor price movements over the same period.
    </>,
    'Business Outcomes': <>
      This metric tracks commercial impact by measuring the percentage change in conversion rate
      (CVR) to assess market-level traffic quality.
    </>,
  }
}


export type BenchmarkingTableProps = {
  rows: BenchmarkingTableRow[]
  costEffectivenessMetric?: string | null
}

export const BenchmarkingTable = ({
  rows,
  costEffectivenessMetric,
}: BenchmarkingTableProps): JSX.Element => {

  const tooltipContentMapping = getTooltipContentMapping(costEffectivenessMetric)

  const [activeColumnKeys, setActiveColumnKeys] = useState<(keyof BenchmarkingTableRow)[]>(
    columns.filter( c => !c.hideInitially ).map( c => c.key )
  )

  const visibleColumns = columns.reduce( (acc, c) => {
    if( !activeColumnKeys.includes(c.key) ){
      return acc
    }
    acc.push({ ...c, tooltip: tooltipContentMapping[c.key] })
    return acc
  }, [] as Column<BenchmarkingTableRow>[])

  const tableClasses = useStyles()

  const columnGroupings: {
    grouping: ColumnGrouping
    labelOverride?: string | null
    tooltipContent?: React.ReactNode
    className: string
    colSpan: number
  }[] = [
    {
      grouping: 'Cost Effectiveness',
      labelOverride: costEffectivenessMetric && `Cost Effectiveness (${costEffectivenessMetric})`,
      className: tableClasses.effectivenessCell,
      colSpan: intersection(
        activeColumnKeys,
        columnGroupingMapping['Cost Effectiveness'],
      ).length,
      tooltipContent: tooltipContentMapping['Cost Effectiveness'],
    },
    {
      grouping: 'Business Outcomes',
      className: tableClasses.outcomesCell,
      colSpan: intersection(
        activeColumnKeys,
        columnGroupingMapping['Business Outcomes'],
      ).length,
      tooltipContent: tooltipContentMapping['Business Outcomes'],
    }
  ]

  return (
    <>
    <Box display='flex' justifyContent='flex-end' p={2}>
      <ButtonPopover
        ButtonComponent={RoundedPlainTextButton}
        buttonContent='Show / Hide Columns'
        variant='contained'
        size='small'
        startIcon={<ViewColumnSharp />}>
        <Box p={2}>
          <CheckboxGroup
            name='Show Columns'
            disableSelectAll
            value={activeColumnKeys}
            options={
              columns.map( c => ({
                value: c.key,
                label: c.label,
                disabled: !c.filterable,
              }))
            }
            onChange={setActiveColumnKeys} />
        </Box>
      </ButtonPopover>
    </Box>
    <SimpleTable
      pinFirstColumn
      sortable
      stickyHeader
      size='small'
      wrapCellText={false}
      rows={rows}
      columns={visibleColumns}
      renderers={renderers}
      defaultRenderer={defaultRenderer}
      getAdditionalHeaders={({ classes }): JSX.Element => (
        <TableHead
          className={classes.tableHead}>
          <TableCell
            classes={{
              stickyHeader: tableClasses.headerCell,
            }} />
          { columnGroupings.map( ({ grouping, labelOverride, tooltipContent, className, colSpan }) => (
            !colSpan ? null : (
              <TableCell
                key={grouping}
                align='center'
                colSpan={colSpan}
                classes={{
                  stickyHeader: className,
                }}>
                <span className={tableClasses.columGroupCellContent}>
                  { labelOverride || grouping }
                  { tooltipContent && (
                    <SecondaryIconTooltip
                      classes={{
                        tooltip: tableClasses.tooltip,
                      }}
                      title={tooltipContent}
                      IconProps={{
                        className: tableClasses.tooltipIcon,
                      }} />
                  )}
                </span>
              </TableCell>
            )
          ))}
        </TableHead>
      )} />
    </>
  )
}
